import { findTierAndIntervalByBimOfferId } from '@/constants/guild'
import type { Project as ProjectService } from '@/services/ProjectsService'
import {
  isDomestic,
  isUnitedStates,
  isUnitedStatesOrCanada,
  isUnitedStatesOrTerritories,
} from '@/services/UserRegionService'
import type { Episode, GuildRolesQuery, Project, Scalars } from '@/types/codegen-federation'
import type { Maybe } from '../types'

type User = GuildRolesQuery['user']

type EarlyAccessEpisodeProps = {
  earlyAccessDate?: Maybe<Scalars['ISO_DateTime']['output']>
  publiclyAvailableDate?: Maybe<Scalars['ISO_DateTime']['output']>
  unavailableReason?: Maybe<string>
}

export interface GuildPlanSelectionNew {
  currency?: string
  price: number
  monthlyPrice: number
  tier: 'basic' | 'premium'
}

/**
 * There are some cases where we only want to promote the guild to users where the guild is 100% supported.
 * This function should be used in those cases.
 * If region is undefined, it will return false.
 * @param region
 * @returns
 */
export function isGuildFullySupported(region: Maybe<string>): boolean {
  return isUnitedStatesOrTerritories(region)
}

/**
 * This function should be used in most places to determine if the guild should be promoted to the user.
 * Regions that return true for this function will have nearly full guild access.
 * If region is undefined, it will return false.
 * @param region
 * @returns
 */
export function isGuildMostlySupported(region: Maybe<string>): boolean {
  return isDomestic(region)
}

export function canShipForFree(region: Maybe<string>): boolean {
  return isUnitedStates(region)
}

export function shouldPromoteFreeMovieTickets(region: Maybe<string>): boolean {
  return isUnitedStatesOrCanada(region)
}

/**
 * Whether or not to show availability warnings for guild related features.
 * Importantly, this function will return true if the region is undefined to err on the side of caution.
 * @param region
 * @returns
 */
export function shouldShowAvailabilityWarning(region: Maybe<string>): boolean {
  return !isGuildMostlySupported(region)
}

export function isEarlyAccessEpisodeAndAccessible(isGuildTierExperimentOn: boolean, user: User, episode: Episode) {
  const doesUserHaveEarlyAccess = isGuildTierExperimentOn
    ? hasEarlyAccessRole(user)
    : Boolean(episode?.userHasEarlyAccess)

  return isEarlyAccessEpisode(episode) && doesUserHaveEarlyAccess
}

const UPSELL_REASONS = new Set(['PRERELEASE', 'PRESALE']) as Set<string>
export function isEarlyAccessEpisode(episode: EarlyAccessEpisodeProps | undefined, isGuildMember?: boolean): boolean {
  if (!isGuildMember && !episode?.unavailableReason) return false
  const hasEarlyAccessDate = !!episode?.earlyAccessDate
  const isSameDate = episode?.earlyAccessDate === episode?.publiclyAvailableDate

  let isRestricted = true
  if (!isGuildMember) {
    isRestricted = Boolean(episode?.unavailableReason && UPSELL_REASONS.has(episode.unavailableReason))
  }

  return hasEarlyAccessDate && isRestricted && !isSameDate
}

export function getProjectInEarlyAccess(data: Project | ProjectService, isGuildMember?: boolean) {
  const firstSeason = data?.seasons?.[0]
  const firstEpisode = firstSeason?.episodes?.[0]
  const lastSeason = data?.seasons?.[data?.seasons?.length - 1]
  const lastEpisode = lastSeason?.episodes?.[lastSeason?.episodes?.length - 1]
  return Boolean(
    (firstSeason && firstEpisode && isEarlyAccessEpisode(firstEpisode, isGuildMember)) ||
      (lastSeason && lastEpisode && isEarlyAccessEpisode(lastEpisode, isGuildMember)),
  )
}

/**
 * Uses new guildRoles from guild_membership_tiers_experiment
 */
export function hasEarlyAccessRole(user?: User): boolean {
  return Boolean(user?.guildRoles?.hasEarlyAccess)
}

export function isPremiumYearly(offerId: string): boolean {
  const planInfo = findTierAndIntervalByBimOfferId(offerId)

  if (!planInfo) return false

  return planInfo.tier === 'premium' && planInfo.interval === 'annual'
}
